// ImpersonationContext — sandbox impersonation for super admins.
// When impersonating, the admin can fully interact with the system as the
// impersonated user, but ALL mutations happen in-memory only (sandbox mode).
// No data is persisted to the database. When impersonation ends, the sandbox
// is discarded and everything reverts.
//
// The real auth session (JWT) is never changed — Supabase calls for reading
// data use the admin's token. Audit logs for impersonation start/end still
// fire under the real admin actor_id.

import React, { createContext, useCallback, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { setImpersonating as setSafeImpersonating } from '@/lib/safeSupabase';
import type { AppRole } from '@/hooks/useAuth';
import type { TaxScenario, TaxScenarioInput } from '@/tax/types';
import { rowToScenario } from '@/tax/scenarioStore';
import {
  initSandbox,
  clearSandbox,
  getSandboxState,
  sandboxLoadScenarios,
  sandboxLoadDrafts,
  sandboxCreateScenario,
  sandboxSaveDraft,
  sandboxUpdateScenario,
  sandboxUpdateDraft,
  sandboxFinalizeDraft,
  sandboxDeleteScenario,
  sandboxDuplicateScenario,
  sandboxReorderScenarios,
} from '@/lib/sandboxStore';

export interface ImpersonationState {
  isImpersonating: boolean;
  impersonatedUserId: string | null;
  impersonatedProfile: { full_name: string; email: string; org_id: string | null; status: string; has_client_book?: boolean } | null;
  impersonatedOrg: any | null;
  impersonatedRoles: AppRole[];
  /** is_platform_staff_org for the IMPERSONATED org — useAuth must never fall
   * back to the admin's own answer here (2026-09-04 incident). */
  impersonatedIsPlatformStaffOrg: boolean;
}

export interface SandboxActions {
  loadScenarios: () => TaxScenario[];
  loadDrafts: () => TaxScenario[];
  createScenario: (input: TaxScenarioInput, userId: string, clientId?: string) => Promise<TaxScenario>;
  updateScenario: (id: string, input: TaxScenarioInput) => Promise<TaxScenario>;
  deleteScenario: (id: string) => void;
  duplicateScenario: (scenario: TaxScenario, userId: string) => Promise<TaxScenario>;
  saveDraft: (input: TaxScenarioInput, userId: string, clientId?: string) => Promise<TaxScenario>;
  updateDraft: (id: string, input: TaxScenarioInput) => Promise<TaxScenario>;
  finalizeDraft: (id: string, input: TaxScenarioInput) => Promise<TaxScenario>;
  reorderScenarios: (scenarioIds: string[], sortOrders: number[]) => void;
  /** Increment to trigger React Query refetches of sandbox data */
  version: number;
}

interface ImpersonationContextValue extends ImpersonationState {
  startImpersonation: (userId: string) => Promise<void>;
  stopImpersonation: () => void;
  sandbox: SandboxActions | null;
  /**
   * Sandbox simulations for surfaces that don't live in scenarioStore.
   * Components call these instead of supabase when impersonating so the UI
   * reflects the action without hitting the DB. No-ops when not impersonating.
   */
  simulateProfileUpdate: (updates: Partial<{ full_name: string; email: string }>) => void;
}

export const ImpersonationContext = createContext<ImpersonationContextValue | null>(null);

export function ImpersonationProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [state, setState] = useState<ImpersonationState>({
    isImpersonating: false,
    impersonatedUserId: null,
    impersonatedProfile: null,
    impersonatedOrg: null,
    impersonatedRoles: [],
    impersonatedIsPlatformStaffOrg: false,
  });
  const [sandboxVersion, setSandboxVersion] = useState(0);

  const bumpVersion = useCallback(() => setSandboxVersion(v => v + 1), []);

  // Wipe every query cache that's scoped to the user/sandbox so a re-impersonation
  // of the same user can't serve stale data from a previous session (e.g. a
  // scenario you "deleted" in-sandbox staying gone after stop+restart). Run on
  // both ENTRY and EXIT to clean both directions.
  const resetUserScopedCaches = useCallback(() => {
    queryClient.removeQueries({ queryKey: ['scenarios'] });
    queryClient.removeQueries({ queryKey: ['drafts'] });
    queryClient.removeQueries({ queryKey: ['org-scenarios'] });
    queryClient.removeQueries({ queryKey: ['my-assignments'] });
  }, [queryClient]);

  const startImpersonation = useCallback(async (userId: string) => {
    // Impersonation is SUPER ADMIN ONLY. Platform admins must never impersonate:
    // impersonatedRoles feed useAuth, so impersonating a super admin would hand
    // the caller the full super-admin UI — a privilege escalation.
    const { data: { user: authUser } } = await supabase.auth.getUser();
    if (!authUser) throw new Error('Forbidden: not authenticated');
    const { data: adminRoles } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', authUser.id)
      .eq('role', 'super_admin');
    if (!adminRoles || adminRoles.length === 0) throw new Error('Forbidden: super admin role required');

    const [profileRes, rolesRes] = await Promise.all([
      supabase.from('profiles').select('*').eq('id', userId).single(),
      supabase.from('user_roles').select('role').eq('user_id', userId),
    ]);
    if (profileRes.error) throw profileRes.error;
    if (rolesRes.error) throw rolesRes.error;

    const profileData = profileRes.data
      ? { full_name: profileRes.data.full_name ?? '', email: profileRes.data.email ?? '', org_id: profileRes.data.org_id ?? null, status: profileRes.data.status ?? 'active', has_client_book: (profileRes.data as { has_client_book?: boolean }).has_client_book }
      : null;
    const userRoles = (rolesRes.data ?? []).map(r => r.role as AppRole);

    let orgData: any = null;
    if (profileData?.org_id) {
      const [orgRes, countRes] = await Promise.all([
        supabase.from('organizations')
          .select('id, name, slug, owner_id, subscription_status, subscription_plan, max_seats, trial_ends_at')
          .eq('id', profileData.org_id).single(),
        supabase.from('profiles').select('*', { count: 'exact', head: true }).eq('org_id', profileData.org_id),
      ]);
      if (orgRes.error) throw orgRes.error;
      if (!orgRes.data) throw new Error(`Organization ${profileData.org_id} not found for user ${userId}`);
      orgData = { ...orgRes.data, member_count: countRes.count ?? 1 };
    }

    // The sidebar's Account-lane gate is per-org. Compute it for THEIR org
    // through the real client (this file never goes through the sandbox
    // guard). On any RPC failure fall back to their own roles — platform
    // staff live in a platform-staff org by definition.
    let isPlatformStaffOrg = userRoles.includes('super_admin') || userRoles.includes('platform_admin');
    if (orgData) {
      const { data: staffOrg, error: staffOrgErr } = await (supabase.rpc as unknown as (
        fn: 'is_platform_staff_org',
        args: { p_org: string },
      ) => Promise<{ data: boolean | null; error: Error | null }>)('is_platform_staff_org', { p_org: orgData.id });
      if (!staffOrgErr) isPlatformStaffOrg = !!staffOrg;
    }

    // Load the impersonated user's scenarios & drafts into the sandbox
    const scenarioQuery = supabase.from('scenarios').select('*').eq('is_draft', false).eq('user_id', userId).order('sort_order', { ascending: true }).order('created_at', { ascending: true });
    const draftQuery = supabase.from('scenarios').select('*').eq('is_draft', true).eq('user_id', userId).order('updated_at', { ascending: false });
    const [scenariosRes, draftsRes] = await Promise.all([scenarioQuery, draftQuery]);
    const scenarios = (scenariosRes.data ?? []).map(rowToScenario);
    const drafts = (draftsRes.data ?? []).map(rowToScenario);

    initSandbox(scenarios, drafts);
    setSandboxVersion(0);

    // Flip the safeSupabase guard BEFORE setState so any synchronous render
    // pass that fires next can't sneak a write through.
    setSafeImpersonating(true);

    // Drop any stale user-scoped cache from a previous session before the
    // re-render fires its queryFns.
    resetUserScopedCaches();

    setState({
      isImpersonating: true,
      impersonatedUserId: userId,
      impersonatedProfile: profileData,
      impersonatedOrg: orgData,
      impersonatedRoles: userRoles,
      impersonatedIsPlatformStaffOrg: isPlatformStaffOrg,
    });

    // Land on THEIR home page. Impersonation is usually started from inside
    // the admin panel, and without this you'd simply stay there — seeing the
    // panel instead of what the impersonated user actually sees. (A user with
    // no home page of their own, i.e. another super admin, gets bounced back
    // to /super-admin by Index's own redirect.)
    navigate('/', { replace: true });

    // Audit the start of impersonation (uses real admin session)
    supabase.rpc('log_audit', {
      p_action: 'impersonation.started',
      p_org_id: profileData?.org_id ?? null,
      p_target_type: 'user',
      p_target_id: userId,
      p_details: { email: profileData?.email },
    }).then(() => {});
  }, [resetUserScopedCaches, navigate]);

  const stopImpersonation = useCallback(() => {
    setState(prev => {
      if (prev.isImpersonating) {
        supabase.rpc('log_audit', {
          p_action: 'impersonation.ended',
          p_org_id: prev.impersonatedOrg?.id ?? null,
          p_target_type: 'user',
          p_target_id: prev.impersonatedUserId,
          p_details: {},
        }).then(() => {});
      }
      return {
        isImpersonating: false,
        impersonatedUserId: null,
        impersonatedProfile: null,
        impersonatedOrg: null,
        impersonatedRoles: [],
    impersonatedIsPlatformStaffOrg: false,
        impersonatedIsPlatformStaffOrg: false,
      };
    });
    clearSandbox();
    setSandboxVersion(0);
    // Lift the guard so the admin can mutate their own data again.
    setSafeImpersonating(false);
    // Wipe any cached sandbox query results so a future re-impersonation
    // doesn't flash the previous session's mutated state.
    resetUserScopedCaches();
    // Return to the admin panel. Only super admins can impersonate, and they
    // have no workspace of their own — leaving them wherever the impersonated
    // user happened to be (Team Management, a client view) strands them in a
    // shell that isn't theirs.
    navigate('/super-admin', { replace: true });
  }, [resetUserScopedCaches, navigate]);

  // Sandbox actions — wrappers that bump version after each mutation
  const sandbox: SandboxActions | null = state.isImpersonating
    ? {
        loadScenarios: sandboxLoadScenarios,
        loadDrafts: sandboxLoadDrafts,
        createScenario: async (input, userId, clientId?) => {
          const s = await sandboxCreateScenario(input, userId, clientId);
          bumpVersion();
          return s;
        },
        updateScenario: async (id, input) => {
          const s = await sandboxUpdateScenario(id, input);
          bumpVersion();
          return s;
        },
        deleteScenario: (id) => {
          sandboxDeleteScenario(id);
          bumpVersion();
        },
        duplicateScenario: async (scenario, userId) => {
          const s = await sandboxDuplicateScenario(scenario, userId);
          bumpVersion();
          return s;
        },
        saveDraft: async (input, userId, clientId?) => {
          const s = await sandboxSaveDraft(input, userId, clientId);
          bumpVersion();
          return s;
        },
        updateDraft: async (id, input) => {
          const s = await sandboxUpdateDraft(id, input);
          bumpVersion();
          return s;
        },
        finalizeDraft: async (id, input) => {
          const s = await sandboxFinalizeDraft(id, input);
          bumpVersion();
          return s;
        },
        reorderScenarios: (scenarioIds, sortOrders) => {
          sandboxReorderScenarios(scenarioIds, sortOrders);
          bumpVersion();
        },
        version: sandboxVersion,
      }
    : null;

  const simulateProfileUpdate = useCallback(
    (updates: Partial<{ full_name: string; email: string }>) => {
      // No-op outside impersonation — surfaces always call this; the guard
      // is here, not at every call site.
      if (!state.isImpersonating || !state.impersonatedProfile) return;
      setState(prev => ({
        ...prev,
        impersonatedProfile: prev.impersonatedProfile
          ? { ...prev.impersonatedProfile, ...updates }
          : prev.impersonatedProfile,
      }));
    },
    [state.isImpersonating, state.impersonatedProfile],
  );

  return (
    <ImpersonationContext.Provider value={{ ...state, startImpersonation, stopImpersonation, sandbox, simulateProfileUpdate }}>
      {children}
    </ImpersonationContext.Provider>
  );
}

export function useImpersonation() {
  const ctx = useContext(ImpersonationContext);
  // Returns safe default if called outside provider
  return ctx ?? {
    isImpersonating: false,
    impersonatedUserId: null,
    impersonatedProfile: null,
    impersonatedOrg: null,
    impersonatedRoles: [] as AppRole[],
    impersonatedIsPlatformStaffOrg: false,
    startImpersonation: async (_: string) => {},
    stopImpersonation: () => {},
    sandbox: null,
    simulateProfileUpdate: () => {},
  };
}
